import { getEmptyFormSoftSkillValues } from 'components/feature/jobRole/JobRoleForm/JobRoleForm.helpers';
import {
  IJobRoleFormSubmitSoftSkillTraitValues,
  IJobRoleFormSubmitValues,
  TJobRoleFormPredefinedValues,
  TJobRoleFormSoftSkillTraitValues,
  TJobRoleFormSoftSkillValues,
  TTechnicalSkillValue,
} from 'components/feature/jobRole/JobRoleForm/JobRoleForm.types';
import { orientationOptions } from 'components/feature/jobRole/TraitsFormItem/TraitsFormItem';
import { getValueFromCompany } from 'components/molecules/SelectFormField/CompanySelectFormField/CompanySelectFormField';
import { appRoutes } from 'constants/navigation';
import Decimal from 'decimal.js';
import {
  CalculationMethod,
  GradingMethod,
  JobRoleFamily,
  JobRoleFragment,
  JobRoleUpdateOneMutationVariables,
  MeasurementConstructType,
  Orientation,
  ProjectCompanyFragment,
  SoftSkillTraitFragment,
  SuccessProfileFragment,
  SuccessProfileSoftSkillTraitFragment,
  useCreateSuccessProfileMutation,
  useJobRoleFindOneQuery,
  useJobRoleUpdateOneMutation,
  useSuccessProfileSoftSkillsOverwriteMutation,
  useSuccessProfileTechnicalSkillsOverwriteMutation,
  useUpdateSuccessProfileMutation,
} from 'generated/graphql';
import { LocationState, useGetProjectFromLocation } from 'helpers/jobRole';
import { TEditJobRolePath } from 'interfaces/routes';
import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import {
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import EditJobRolePresentational from './components/EditJobRolePresentational/EditJobRolePresentational';
import styles from './EditJobRole.module.scss';

const EditJobRole: FC = () => {
  const navigate = useNavigate();
  const params = useParams() as TEditJobRolePath;
  const { handleMsgType } = useNotification();

  const { projectPath, projectQueryResponse } = useGetProjectFromLocation();
  const projectJobRoleSkillsProfileId =
    useGetProjectJobRoleSkillsProfileFromLocation();
  const projectId = projectQueryResponse?.data?.project?.id;
  const jobRoleId = Number(params.jobRoleId);

  const [saveLoading, setSaveLoading] = useState(false);

  const defaultErrorHandler = (error?: ApolloError) => {
    handleMsgType({
      type: TNotification.error,
      message: (error as ApolloError)?.message || 'Oops, some issue occurred!',
    });

    projectPath ? navigate(projectPath) : navigate(appRoutes.projects.url());
  };

  useEffect(() => {
    if (!params.jobRoleId) defaultErrorHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.jobRoleId]);

  // Getting initial data.
  const jobRoleFindOneQueryResponse = useJobRoleFindOneQuery({
    variables: {
      id: jobRoleId,
    },
    skip: !params.jobRoleId,
    onError: defaultErrorHandler,
  });
  const jobRole = jobRoleFindOneQueryResponse.data?.JobRoleFindOne;
  const skillsProfile = useMemo(
    () =>
      jobRole?.skillsProfiles.find(
        (skillsProfile) => skillsProfile.id === projectJobRoleSkillsProfileId,
      ),
    [jobRole?.skillsProfiles, projectJobRoleSkillsProfileId],
  );

  // Initialize mutations required for submitting form values.
  const [jobRoleUpdateOneMutation] = useJobRoleUpdateOneMutation({
    fetchPolicy: 'no-cache',
  });

  const [updateSuccessProfileMutation] = useUpdateSuccessProfileMutation({
    fetchPolicy: 'no-cache',
  });

  const [createSuccessProfileMutation] = useCreateSuccessProfileMutation({
    fetchPolicy: 'no-cache',
  });

  const [updateTechnicalSkillsMutation] =
    useSuccessProfileTechnicalSkillsOverwriteMutation();

  const [updateSoftSkillsMutation] =
    useSuccessProfileSoftSkillsOverwriteMutation();

  // Helpers required for submitting form values.
  const jobRoleUpdateOne = (formValues: IJobRoleFormSubmitValues) => {
    // Job role will be updated only if the form values are different from the current job role.
    const jobRoleParamsCandidate = {
      id: Number(jobRoleId),
      qualifier: formValues.additionalInformation,
      name: formValues.jobRoleName,
      roleLevel: formValues.roleLevel,
      family: formValues.family.value as JobRoleFamily,
    };

    const jobRoleParams: JobRoleUpdateOneMutationVariables = Object.entries(
      jobRoleParamsCandidate,
    ).reduce(
      (acc, [key, value]) => {
        const skip =
          key === 'id' ||
          value === jobRole?.[key as keyof JobRoleUpdateOneMutationVariables];
        return skip ? acc : { ...acc, [key]: value };
      },
      {
        id: jobRoleParamsCandidate.id,
      },
    );

    // The id is required parameter, so we need to check if any other parameter is provided.
    if (Object.keys(jobRoleParams).length === 1) return;

    return jobRoleUpdateOneMutation({
      variables: jobRoleParams,
    });
  };

  const createSkillsProfile = (formValues: IJobRoleFormSubmitValues) => {
    return createSuccessProfileMutation({
      variables: {
        calculationMethod: formValues.calculationMethod as CalculationMethod,
        gradingMethod: formValues.gradingMethod as GradingMethod,
        jobRoleId: jobRoleId,
      },
    });
  };

  const updateSkillsProfile = (formValues: IJobRoleFormSubmitValues) => {
    if (!projectJobRoleSkillsProfileId) return;

    return updateSuccessProfileMutation({
      variables: {
        id: projectJobRoleSkillsProfileId,
        calculationMethod: formValues.calculationMethod as CalculationMethod,
        gradingMethod: formValues.gradingMethod as GradingMethod,
      },
    });
  };

  const parseTraitsFormValues = (
    traits: IJobRoleFormSubmitSoftSkillTraitValues[],
  ) => {
    return traits
      .filter((traitValues) => traitValues.weight)
      .map((traitValues) => ({
        traitId: traitValues.traitId,
        weight: traitValues.weight,
        orientation: traitValues.orientation,
      }));
  };

  const updateSoftSkills = (
    skillsProfileId: number,
    formValues: IJobRoleFormSubmitValues,
  ) => {
    const parseSoftSkillValues = formValues.softSkills.map(
      (softSkillValues) => ({
        weight: softSkillValues.weight,
        softSkillId: softSkillValues.softSkillId,
        type: softSkillValues.type,
        includeInCalibration: softSkillValues.includeInCalibration,
        traits: parseTraitsFormValues(softSkillValues.traitsValues),
      }),
    );

    return updateSoftSkillsMutation({
      variables: {
        id: skillsProfileId,
        softSkills: parseSoftSkillValues,
      },
    });
  };

  const updateTechnicalSkills = (
    skillsProfileId: number,
    formValues: IJobRoleFormSubmitValues,
  ) => {
    return updateTechnicalSkillsMutation({
      variables: {
        id: skillsProfileId,
        technicalSkills: formValues.technicalSkills.map(
          (technicalSkillValue) => ({
            technicalSkillId: Number(technicalSkillValue.value),
          }),
        ),
      },
    });
  };

  const handleEditJobRole = async (formValues: IJobRoleFormSubmitValues) => {
    setSaveLoading(true);

    // TODO: Remove this constraint after projectId is not required for creating success profile.
    if (!projectId) {
      setSaveLoading(false);
      return handleMsgType({
        type: TNotification.error,
        message: 'Oops, role requires to be connected to the project!',
      });
    }

    if (!jobRole) return;

    try {
      await jobRoleUpdateOne(formValues);

      const skillsProfileRes = await (skillsProfile
        ? updateSkillsProfile(formValues)
        : createSkillsProfile(formValues));

      if (skillsProfileRes?.data?.successProfile) {
        const skillsProfileId = skillsProfileRes.data.successProfile
          .id as number;

        formValues.softSkills.length &&
          (await updateSoftSkills(skillsProfileId, formValues));

        await updateTechnicalSkills(skillsProfileId, formValues);
      }

      await jobRoleFindOneQueryResponse.refetch();

      handleMsgType({
        type: TNotification.success,
        message: 'Role updated!',
      });

      if (projectPath) {
        navigate(projectPath);
      } else {
        setSaveLoading(false);
      }
    } catch (error) {
      setSaveLoading(false);
      handleMsgType({
        type: TNotification.error,
        message:
          (error as ApolloError)?.message ||
          'Oops, some issue occurred while creating Role!',
      });
    }
  };

  const handleCancel = () => {
    if (projectPath) {
      navigate(projectPath);
    }
  };

  if (
    projectQueryResponse?.loading ||
    jobRoleFindOneQueryResponse.loading ||
    saveLoading
  ) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader variant="bubbles" />
      </div>
    );
  }

  if (jobRole) {
    const project = projectQueryResponse?.data?.project;

    const initialValues = getInitialValues({
      jobRole,
      projectCompany: project?.company,
      skillsProfile,
    });

    const predefinedSoftSkills =
      skillsProfile?.successProfileSoftSkills?.map(
        (softSkill) => softSkill.softSkill,
      ) || [];

    return (
      <EditJobRolePresentational
        jobRoleCreatedAt={jobRole.createdAt}
        jobRoleUpdatedAt={jobRole.updatedAt}
        initialValues={initialValues}
        onCancel={handleCancel}
        onSubmit={handleEditJobRole}
        projectName={project?.name}
        projectCompanyName={project?.company?.name}
        projectPath={projectPath}
        predefinedSoftSkills={predefinedSoftSkills}
      />
    );
  }

  return null;
};

function useGetProjectJobRoleSkillsProfileFromLocation() {
  const locationState = useLocation().state as LocationState;

  return locationState.projectJobRoleSkillsProfileId;
}

// Making form initial values from API data helpers. (TODO: To refactor)

function initializeTechnicalSkillsValues(
  successProfile: SuccessProfileFragment,
): TTechnicalSkillValue[] {
  const techSkills = successProfile?.successProfileTechnicalSkills?.map(
    ({ technicalSkill }) => ({
      value: `${technicalSkill?.id}` || '0',
      label: technicalSkill?.name || '',
    }),
  );

  return techSkills || [];
}

function initializeTraitsValues(
  ssTrait: SoftSkillTraitFragment,
  spTrait?: SuccessProfileSoftSkillTraitFragment,
) {
  const weight = spTrait?.weight ?? 0;
  const orientationIndex = spTrait
    ? spTrait.orientation ?? 0
    : ssTrait.orientation === Orientation.Standard
    ? 1
    : 0;

  return {
    id: ssTrait.id || 0,
    traitId: ssTrait.traitId || 0,
    name: ssTrait.trait?.name || ssTrait.traitName || '',
    weight: new Decimal(weight).times(100).toString(),
    orientation: orientationOptions[orientationIndex],
  };
}

function initializeSoftSkillsValues(
  successProfile: SuccessProfileFragment,
): TJobRoleFormSoftSkillValues[] {
  const parsedSoftSkills: TJobRoleFormSoftSkillValues[] =
    successProfile.successProfileSoftSkills?.map((spSoftSkill) => {
      /**
       * we don't want users to edit traits in right for us, right for the role and right for the future soft skills.
       * These traits are picked by backend implicitly
       */

      const customTraitsByTraitId = new Map(
        spSoftSkill.successProfileSoftSkillTraits?.map((spTrait) => [
          spTrait.traitId,
          spTrait,
        ]),
      );

      const traitsValues: TJobRoleFormSoftSkillTraitValues[] =
        spSoftSkill?.softSkill?.measurementConstructType ===
        MeasurementConstructType.CalculatedOnSsSummary
          ? []
          : spSoftSkill.softSkill?.softSkillTraits?.map((ssTrait) =>
              initializeTraitsValues(
                ssTrait,
                ssTrait.traitId
                  ? customTraitsByTraitId.get(ssTrait.traitId)
                  : undefined,
              ),
            ) || [];

      return {
        softSkillId: { value: `${spSoftSkill.softSkillId}` },
        name: spSoftSkill.name,
        type: { value: spSoftSkill.type },
        weight: new Decimal(spSoftSkill.weight).times(100).toString(),
        traitsValues,
        includeInCalibration: spSoftSkill.includeInCalibration,
      };
    }) || [];

  return [...parsedSoftSkills, getEmptyFormSoftSkillValues()];
}

type TGetInitialValuesInput = {
  jobRole: JobRoleFragment;
  projectCompany?: ProjectCompanyFragment | null;
  skillsProfile?: SuccessProfileFragment;
};

function getInitialValues({
  jobRole,
  projectCompany,
  skillsProfile,
}: TGetInitialValuesInput): TJobRoleFormPredefinedValues {
  return {
    customWeights: true,
    jobRoleName: jobRole.name,
    roleLevel: { value: jobRole.roleLevel },
    company: projectCompany ? getValueFromCompany(projectCompany) : undefined,
    additionalInformation: jobRole.qualifier || '',
    family: { value: jobRole.family || '' },
    softSkills: skillsProfile
      ? initializeSoftSkillsValues(skillsProfile)
      : undefined,
    technicalSkills: skillsProfile
      ? initializeTechnicalSkillsValues(skillsProfile)
      : undefined,
  };
}

export default EditJobRole;
